export interface SEOConfig {
  title: string;
  description: string;
  canonical?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  noindex?: boolean;
  keywords?: string;
}

export const SITE_URL = 'https://autonomoclaro.com';
export const SITE_NAME = 'Autónomo Claro';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/logo.png`;

export const seoConfig: Record<string, SEOConfig> = {
  home: {
    title: 'Autónomo Claro | Calculadora IRPF, RETA e IVA para Autónomos 2025/2026',
    description: 'Calcula IRPF, cuota de autónomos (RETA) e IVA con una calculadora para autónomos en España actualizada para 2025/2026. Simula neto final, renta, anticipos y trimestres.',
    canonical: SITE_URL,
    ogType: 'website',
    keywords: 'calculadora autónomos, calculadora irpf autonomos, cuota autónomos, cuota de autónomos, RETA 2026, IVA autónomos, simulador fiscal autónomos',
  },
  calculator: {
    title: 'Calculadora Fiscal Autónomos 2025 | IRPF, cuota de autónomos e IVA',
    description: 'Herramienta profesional para calcular IRPF, cuota de autónomos (RETA) e IVA. Incluye deducciones, retenciones y simulación anual trimestral.',
    canonical: `${SITE_URL}/calculadora`,
    ogType: 'website',
    keywords: 'calcular IRPF autónomos, cuota de autónomos, IVA autónomos, simulador fiscal',
  },
  guideIRPF: {
    title: 'Guía IRPF para Autónomos 2025 | Tramos, Retenciones y Deducciones',
    description: 'Guía completa del IRPF para autónomos: tramos impositivos 2025, cómo calcular retenciones, deducciones aplicables y modelo 130. Todo lo que necesitas saber.',
    canonical: `${SITE_URL}/guia/irpf-autonomos`,
    ogType: 'article',
    keywords: 'IRPF autónomos, tramos IRPF 2025, retenciones autónomos, modelo 130',
  },
  guideRETA: {
    title: 'Cuota de autónomos (RETA) 2025 | Guía Completa de Cotización',
    description: 'Todo sobre la cuota de autónomos (RETA) 2025: sistema de cotización por ingresos reales, tramos, bonificaciones y cómo elegir tu base de cotización.',
    canonical: `${SITE_URL}/guia/cuota-autonomos-reta`,
    ogType: 'article',
    keywords: 'cuota autónomos, cuota de autónomos, RETA 2025, cotización autónomos, base cotización',
  },
  guideModelo130: {
    title: 'Modelo 130 Autónomos 2025 | Qué es y Cómo Presentarlo',
    description: 'Guía del modelo 130: qué es, quién debe presentarlo, plazos trimestrales, cómo calcular el pago fraccionado del IRPF y casos prácticos.',
    canonical: `${SITE_URL}/guia/modelo-130`,
    ogType: 'article',
    keywords: 'modelo 130, pago fraccionado IRPF, declaración trimestral autónomos',
  },
  guideDeducciones: {
    title: 'Deducciones Fiscales para Autónomos 2025 | Gastos Deducibles',
    description: 'Lista completa de gastos deducibles para autónomos: suministros, vehículo, dietas, formación, material y más. Reduce tu factura fiscal legalmente.',
    canonical: `${SITE_URL}/guia/deducciones-autonomos`,
    ogType: 'article',
    keywords: 'deducciones autónomos, gastos deducibles, reducir impuestos autónomos',
  },
  login: {
    title: 'Iniciar Sesión | Autónomo Claro',
    description: 'Accede a tu cuenta de Autónomo Claro para guardar simulaciones y acceder a funciones premium.',
    canonical: `${SITE_URL}/login`,
    noindex: true,
  },
  signup: {
    title: 'Crear Cuenta | Autónomo Claro',
    description: 'Crea tu cuenta de Autónomo Claro para guardar simulaciones y acceder a funciones premium.',
    canonical: `${SITE_URL}/signup`,
    noindex: true,
  },
  profile: {
    title: 'Mi Perfil | Autónomo Claro',
    description: 'Gestiona tu perfil y simulaciones guardadas.',
    canonical: `${SITE_URL}/profile`,
    noindex: true,
  },
  privacy: {
    title: 'Política de Privacidad | Autónomo Claro',
    description: 'Política de privacidad y protección de datos de Autónomo Claro. Información sobre GDPR y tratamiento de datos personales.',
    canonical: `${SITE_URL}/privacy`,
  },
  cookies: {
    title: 'Política de Cookies | Autónomo Claro',
    description: 'Información sobre el uso de cookies en Autónomo Claro. Cómo gestionar tus preferencias de cookies.',
    canonical: `${SITE_URL}/cookies`,
  },
  terms: {
    title: 'Términos y Condiciones | Autónomo Claro',
    description: 'Términos y condiciones de uso de Autónomo Claro. Lee nuestras condiciones antes de usar la plataforma.',
    canonical: `${SITE_URL}/terms`,
  },
  legal: {
    title: 'Aviso Legal | Autónomo Claro',
    description: 'Aviso legal de Autónomo Claro. Información sobre titularidad, responsabilidades y uso de la plataforma.',
    canonical: `${SITE_URL}/legal`,
  },
};

export function generateMetaTags(config: SEOConfig): string {
  const tags = [
    `<title>${config.title}</title>`,
    `<meta name="description" content="${config.description}" />`,
  ];

  if (config.keywords) {
    tags.push(`<meta name="keywords" content="${config.keywords}" />`);
  }

  if (config.canonical) {
    tags.push(`<link rel="canonical" href="${config.canonical}" />`);
  }

  if (config.noindex) {
    tags.push(`<meta name="robots" content="noindex,nofollow" />`);
  } else {
    tags.push(`<meta name="robots" content="index,follow" />`);
  }

  tags.push(`<meta property="og:title" content="${config.title}" />`);
  tags.push(`<meta property="og:description" content="${config.description}" />`);
  tags.push(`<meta property="og:type" content="${config.ogType || 'website'}" />`);

  if (config.canonical) {
    tags.push(`<meta property="og:url" content="${config.canonical}" />`);
  }

  tags.push(`<meta property="og:image" content="${config.ogImage || DEFAULT_OG_IMAGE}" />`);
  tags.push(`<meta property="og:site_name" content="${SITE_NAME}" />`);
  tags.push(`<meta property="og:locale" content="es_ES" />`);

  tags.push(`<meta name="twitter:card" content="summary_large_image" />`);
  tags.push(`<meta name="twitter:title" content="${config.title}" />`);
  tags.push(`<meta name="twitter:description" content="${config.description}" />`);
  tags.push(`<meta name="twitter:image" content="${config.ogImage || DEFAULT_OG_IMAGE}" />`);

  return tags.join('\n');
}
